import BrandVendor from 'data/BrandVendor';
import * as Prod from 'data/Product';
import { Role } from 'data/User';
import Variant, { VariantAttributeValue, emptyValue, empty } from 'data/Variant';
import Currency from 'data/Currency';
import { TEXT_LABELS } from 'constants/productDetails';
import { PRICE_TYPE } from 'constants/product';
import { CURRENCY_ISO, COUNTRY_ISO } from 'constants/country';
import Country from 'data/Country';
import { PRODUCT_VARIANT_FIELDS_CONFIG } from 'constants/variant';
import { UnitOfMeasurement } from 'data/UnitOfMeasurement';
import { getItems } from 'lib/SessionStorage';
import { STORAGE_KEYS } from 'constants/general';

export const createVariant = (variant: Record<string, any> = {}, attributeList: Prod.ProductAttribute[] = []): Variant => {
  let variantAttributeValues: VariantAttributeValue[] = variant.attributeValues ? variant.attributeValues : [];

  if (attributeList.length > 0) {
    variantAttributeValues = [];
    for (let i = 0; i < attributeList.length; i++) {
      let attributeId = attributeList[i].id || emptyValue.attributeId;
      variantAttributeValues.push({ ...emptyValue, attributeId, isSelectable: attributeList[i].isSelectable })
    }
  }

  return { ...empty, ...variant, attributeValues: variantAttributeValues, productStocks: [], productVariantPrices: [] };
};

export const formatPrice = (price: number): string => {
  return `$${price.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  })}`;
};

export function fieldName(key: string): string {

  // if (key.split('-')?.length > 1) key = key.split('-')[0];

  switch (key) {
    case 'height':
      return 'Height';
    case 'itemsPerUnit':
      return 'Items per Unit';
    case 'length':
      return 'Length';
    case 'pricePerUnit':
      return 'Price per Unit';
    case 'sku':
      return 'SKU';
    case 'vendorPartNumber':
      return 'Vendor Part Number';
    case 'manufacturingPartNumber':
      return 'Manufacturing Part Number';
    case 'weightPerUnit':
      return 'Weight per Unit';
    case 'weight':
      return 'Weight (kg)';
    case 'width':
      return 'Width';
    case 'hidden':
      return 'Hidden';
    case 'deliveryLeadDays':
      return 'Delivery lead days';
    case 'postOrderShippingCost':
      return 'Post Order Shipping Cost';
    case 'Inventory':
      return 'Inventory';
    case 'available':
      return 'In Stock';
    case 'itemType':
      return 'Item Type';
    case 'materialNumber':
      return 'Material Number';
    case 'rent':
      return 'Rent';
    case 'purchase':
      return 'Purchase';
    case 'range':
      return 'Range';
    // case 'rangeUnits':
    case 'rangeUnit':
      return 'Measurement Unit';
    case 'rentalPrice':
      return 'Rental Price';
    case 'duration':
      return 'Duration (Months)';
    case 'affiliateCommissionUS':
      return 'Commission % (US)';
    case 'commissionAmountUS':
      return 'Commission amount (US)';
    case 'affiliateCommissionCA':
      return 'Commission % (CA)';
    case 'commissionAmountCA':
      return 'Commission amount (CA)';
    default:
      return key;
  }
};

export const getBasicColumns = (brandVendor: BrandVendor, isSlbVendor: boolean = false, role: Role | undefined) => {
  const defaultColumns = ['sku', 'vendorPartNumber', 'weight', 'hidden', 'itemType', 'materialNumber'];

  return defaultColumns;
};

export const getTypeColumns = (rentFlag: boolean) => {
  return rentFlag ? [TEXT_LABELS.PURCHASE, TEXT_LABELS.RENT] : [TEXT_LABELS.PURCHASE];
}

export const getRentalColumnsFn = () => {
  return [TEXT_LABELS.RANGE, TEXT_LABELS.RANGE_PRICE];
}


export const getColumnMetaData = (key: any) => {
  if (key instanceof Object) {
    return {
      key: key.name,
      label: fieldName(key.name!),
      keyId: key.id,
    };
  } else {
    return {
      key,
      label: fieldName(key),
    };
  }
};

export const getTotalProductVariantPrices = (variant: Variant, currencies: Currency[]) => {

  const unitOfMeasurements: UnitOfMeasurement[] = getItems(STORAGE_KEYS.UNIT_OF_MEASUREMENTS) || [];

  let productVariantPrices = variant.productVariantPrices ? variant.productVariantPrices.map(variantPrice => {

    return ({
      id: variantPrice.id || variantPrice.priceId,
      productVariantId: variant.id,
      pricePerUnit: variantPrice.pricePerUnit,
      itemsPerUnit: variantPrice.itemsPerUnit,
      countryId: variantPrice.countryId,
      currencyId: currencies.find(currency => currency.countryId === variantPrice.countryId)?.id,
      priceType: variantPrice.priceType || PRICE_TYPE.PURCHASE,
    })

  }) : []

  if (variant.rentalPriceUS) {
    // let oldMaxRange: number | null = 0;
    let mappedRentalPrices = variant.rentalPriceUS.map(variantPrice => {
      const unitOfMeasurement = unitOfMeasurements.find(uom => uom.displayName === variantPrice.unitOfMeasurement);

      let obj = {
        id: variantPrice.id,
        productVariantId: variant.id,
        pricePerUnit: variantPrice.rentalPrice,
        itemsPerUnit: 0,
        countryId: currencies.find(x => x.currencyIso === CURRENCY_ISO.USD)?.countryId,
        currencyId: currencies.find(currency => currency.currencyIso === CURRENCY_ISO.USD)?.id,
        priceType: PRICE_TYPE.RENT,
        // minRange: oldMaxRange === null ? null : parseInt(oldMaxRange.toString()) + 1,
        // maxRange: variantPrice.range,
        unitOfMeasurementId: unitOfMeasurement?.id,
      }

      // oldMaxRange = variantPrice.range;
      return obj;
    });

    productVariantPrices = [...productVariantPrices, ...mappedRentalPrices]
  }

  if (variant.rentalPriceCA) {
    // let oldMaxRange: number | null = 0;
    let mappedRentalPrices = variant.rentalPriceCA.map(variantPrice => {
      const unitOfMeasurement = unitOfMeasurements.find(uom => uom.displayName === variantPrice.unitOfMeasurement);

      let obj = {
        id: variantPrice.id,
        productVariantId: variant.id,
        pricePerUnit: variantPrice.rentalPrice,
        itemsPerUnit: 0,
        countryId: currencies.find(x => x.currencyIso === CURRENCY_ISO.CAD)?.countryId,
        currencyId: currencies.find(currency => currency.currencyIso === CURRENCY_ISO.CAD)?.id,
        priceType: PRICE_TYPE.RENT,
        // minRange: oldMaxRange === null ? null : parseInt(oldMaxRange.toString()) + 1,
        // maxRange: variantPrice.range,
        unitOfMeasurementId: unitOfMeasurement?.id,
      }

      // oldMaxRange = variantPrice.range;
      return obj;
    });

    productVariantPrices = [...productVariantPrices, ...mappedRentalPrices]
  }

  return productVariantPrices;
}

/**
 * Whether a variant-grid column key is backed by the rental price arrays: 'rentalPrice-US' /
 * 'rentalPrice-CA' (the price) or 'rangeUnit-US' / 'rangeUnit-CA' (the unit of measurement —
 * the key keeps the legacy 'range' name, but it writes unitOfMeasurement).
 */
export const isRentalColumnKey = (columnKey: string): boolean =>
  !!columnKey && (columnKey.includes(TEXT_LABELS.RENTAL_PRICE) || columnKey.includes(TEXT_LABELS.RANGE_UNIT));

/**
 * Writes a rental-grid cell value into the variant's rentalPriceUS / rentalPriceCA array.
 *
 * These are the only grid columns backed by an array rather than a flat field, so the generic
 * `{ ...variant, [columnKey]: value }` update every other column uses would only bolt on a
 * meaningless 'rentalPrice-US' property. Both editors previously worked around that by mutating
 * the array in place via a reference shared with the other copy of the variant list, which
 * silently dropped the value whenever the array started empty and a fresh one had to be created.
 *
 * Returns a new variant; the original is never mutated. A non-rental column key is returned
 * untouched so callers can apply this unconditionally.
 */
export const applyRentalColumnValue = <T extends Variant>(
  variant: T,
  columnKey: string,
  value: string | number | boolean | null
): T => {
  if (!isRentalColumnKey(columnKey)) return variant;

  const countryIso = columnKey.split('-')[1];
  const arrayKey = countryIso === COUNTRY_ISO.CA ? 'rentalPriceCA' : 'rentalPriceUS';
  const field = columnKey.includes(TEXT_LABELS.RANGE_UNIT) ? 'unitOfMeasurement' : 'rentalPrice';

  const existing = variant[arrayKey]?.[0] ?? { id: undefined, rentalPrice: 0 };
  const nextValue = field === 'unitOfMeasurement' ? value?.toString() : value;

  return {
    ...variant,
    [arrayKey]: [{ ...existing, [field]: nextValue }]
  } as T;
};

// export const validateRentalRanges = (rentalRanges: RentalPrice[] | undefined, currencyIso: string, variant: Variant):
//   (EmptyRentalPriceCA | EmptyRentalPriceUS | EmptyRentalRangeCA | EmptyRentalRangeUS | SkippedRentalRange | RentRangeOrder)[] => {
//   let errors: any = [];

//   if (rentalRanges?.length) {
//     let intermediateSkippedRanges = [];

//     for (let i = 1; i < rentalRanges.length; i++) {
//       let currentRange = rentalRanges[i]?.range;
//       let previousRange = rentalRanges[i - 1]?.range;
//       let currentRangePrice = rentalRanges[i]?.rangePrice;
//       let previousRangePrice = rentalRanges[i - 1]?.rangePrice;

//       if (!previousRange) {
//         intermediateSkippedRanges.push({
//           key: `range-${i}-${currencyIso}`,
//           type: VariantErrorType.RentRangeSkipped,
//           variants: [variant]
//         })
//       }

//       if ((previousRange && parseInt(previousRange?.toString()!)) || currentRangePrice) {
//         if (intermediateSkippedRanges.length > 0) {
//           errors = [...errors, ...intermediateSkippedRanges]
//           intermediateSkippedRanges = [];
//         }
//       }

//       if (previousRange && !previousRangePrice) {
//         errors.push({
//           key: `rangePrice-${i}-${currencyIso}`,
//           type: VariantErrorType.MissingValue,
//           variants: [variant],
//         });
//       }

//       if (currentRange && previousRange) {
//         if (parseInt(currentRange.toString()) <= parseInt(previousRange.toString())) {
//           errors.push({
//             key: `range-${i + 1}-${currencyIso}`,
//             type: VariantErrorType.RentRangeOrder,
//             variants: [variant],
//           });
//         }
//       }
//     }

//     let rangeExists = rentalRanges.find(x => x.range);

//     if (!rangeExists && !!variant.rent) {
//       errors.push({
//         key: `range-1-${currencyIso}`,
//         type: VariantErrorType.RequiredRentalRange,
//         variants: [variant],
//       });
//     }
//   }
//   return errors;
// }

export const getPlaceholderValidationStatus = () => {
  return { id: 0, name: 'OK' };
}

export const getProductVariantFieldConfig = (key: string | undefined) => {
  if (!key || !(key in PRODUCT_VARIANT_FIELDS_CONFIG))
    return PRODUCT_VARIANT_FIELDS_CONFIG.default;

  return PRODUCT_VARIANT_FIELDS_CONFIG[key];
}

// Returns the variant's purchase price for the given country if set and greater than zero, otherwise undefined
export const getVariantRegionPurchasePrice = (variant: Variant, countryId?: number): number | undefined => {
  if (countryId == null) return undefined;
  const price = variant.productVariantPrices?.find(
    p => p.countryId === countryId && (!p.priceType || p.priceType === PRICE_TYPE.PURCHASE)
  )?.pricePerUnit;
  return price && price > 0 ? price : undefined;
};

export const getRegionCountryId = (countries: Country[], region: 'US' | 'CA'): number | undefined =>
  countries.find(c => c.countryIso === COUNTRY_ISO[region])?.id;

// Whether the product has a PURCHASE warehouse in the given region — commission only makes sense
// for a region the product is actually sold (purchasable) in, same gate used for purchase-price columns.
export const hasRegionPurchaseWarehouse = (warehouses: Prod.ProductWarehouse[] = [], region: 'US' | 'CA'): boolean =>
  warehouses.some((w: any) => w.type === PRICE_TYPE.PURCHASE && w.address?.country?.countryIso === COUNTRY_ISO[region]);

// Whether a list of (already deduped) purchase countries includes the given region
export const countriesIncludeRegion = (countries: { countryIso?: string }[], region: 'US' | 'CA'): boolean =>
  countries.some(c => c.countryIso === COUNTRY_ISO[region]);

// Commission column keys (amount + %) for a region, gated on the product supporting commission
// columns and having a purchase warehouse in that region; otherwise empty.
export const getCommissionColumnKeys = (region: 'US' | 'CA', showCommissionColumns: boolean, hasRegionPurchase: boolean): string[] =>
  showCommissionColumns && hasRegionPurchase ? [`commissionAmount${region}`, `affiliateCommission${region}`] : [];

// Header column-group definitions (one per region that has commission columns) for the variant table.
export const buildCommissionColumnGroups = (commissionColumnsUS: string[], commissionColumnsCA: string[]) => [
  ...(commissionColumnsUS.length > 0 ? [{
    key: 'affiliateCommissionUSGroup',
    name: 'Affiliate Commission (US)',
    colSpan: commissionColumnsUS.length,
    columns: commissionColumnsUS.map(getColumnMetaData),
  }] : []),
  ...(commissionColumnsCA.length > 0 ? [{
    key: 'affiliateCommissionCAGroup',
    name: 'Affiliate Commission (CA)',
    colSpan: commissionColumnsCA.length,
    columns: commissionColumnsCA.map(getColumnMetaData),
  }] : []),
];

// Flat commission amount ($) derived from a commission percentage and a regional purchase price
export const commissionAmountFromPercentage = (percentage: number, price: number): number =>
  parseFloat(((percentage * price) / 100).toFixed(2));

// SKUs (of variants with a sku set) that have a region-level commission configured but no valid
// (>0) purchase price for that region — saving would leave their commission uncalculable.
// Skipped entirely once the product no longer has a purchase warehouse in that region, since
// commission for a region the product isn't sold in is no longer relevant.
export const getVariantsMissingRegionalPrice = (variants: Variant[], region: 'US' | 'CA', countries: Country[], warehouses: Prod.ProductWarehouse[] = []): string[] => {
  if (!hasRegionPurchaseWarehouse(warehouses, region)) return [];

  const countryId = getRegionCountryId(countries, region);
  const percentageField = region === 'US' ? 'affiliateCommissionUS' : 'affiliateCommissionCA';
  const amountField = region === 'US' ? 'commissionAmountUS' : 'commissionAmountCA';

  return variants
    .filter(v => v.sku && (v[percentageField] != null || v[amountField] != null))
    .filter(v => getVariantRegionPurchasePrice(v, countryId) == null)
    .map(v => v.sku);
};

// SKUs (of variants with a sku set) whose commission would exceed the region's purchase price:
// a commission % over 100, or a flat commission $ amount greater than the price itself.
// Skipped entirely once the product no longer has a purchase warehouse in that region.
export const getVariantsWithInvalidCommission = (variants: Variant[], region: 'US' | 'CA', countries: Country[], warehouses: Prod.ProductWarehouse[] = []): string[] => {
  if (!hasRegionPurchaseWarehouse(warehouses, region)) return [];

  const countryId = getRegionCountryId(countries, region);
  const percentageField = region === 'US' ? 'affiliateCommissionUS' : 'affiliateCommissionCA';
  const amountField = region === 'US' ? 'commissionAmountUS' : 'commissionAmountCA';

  return variants
    .filter(v => v.sku)
    .filter(v => {
      const percentage = v[percentageField];
      const amount = v[amountField];
      const price = getVariantRegionPurchasePrice(v, countryId);
      const percentageInvalid = percentage != null && (percentage < 0 || percentage > 100);
      const amountInvalid = amount != null && price != null && amount > price;
      return percentageInvalid || amountInvalid;
    })
    .map(v => v.sku);
};

// Commission percentage (%) derived from a flat commission amount and a regional purchase price
export const commissionPercentageFromAmount = (amount: number, price: number): number =>
  parseFloat(((amount / price) * 100).toFixed(2));

const commissionValuesDiffer = (current?: number | null, original?: number | null): boolean => {
  if (current == null && original == null) return false;
  if (current == null || original == null) return true;
  return Number(current) !== Number(original);
};

/**
 * Keeps a variant's commission amount ($) and commission percentage (%) consistent per region.
 *
 * When only one of the pair is set the other is derived from it. When both are set, the pair is
 * reconciled against `originalVariant`: whichever side was changed wins and the other is
 * recalculated from the regional purchase price. This is what keeps an AI generated (or imported)
 * change to a single side from leaving the other side holding a stale value.
 *
 * Regions without a valid (> 0) regional purchase price are left untouched, since neither value
 * can be derived without one.
 */
export const syncVariantCommissionFields = (
  variant: Variant,
  countries: Country[],
  originalVariant?: Variant | null
): Variant => {
  let synced = { ...variant };

  (['US', 'CA'] as const).forEach(region => {
    const price = getVariantRegionPurchasePrice(synced, getRegionCountryId(countries, region));
    if (!price || price <= 0) return;

    const percentageKey = region === 'US' ? 'affiliateCommissionUS' : 'affiliateCommissionCA';
    const amountKey = region === 'US' ? 'commissionAmountUS' : 'commissionAmountCA';
    const percentage = synced[percentageKey];
    const amount = synced[amountKey];

    if (percentage == null && amount == null) return;

    if (amount != null && percentage == null) {
      synced = { ...synced, [percentageKey]: commissionPercentageFromAmount(Number(amount), price) };
      return;
    }

    if (percentage != null && amount == null) {
      synced = { ...synced, [amountKey]: commissionAmountFromPercentage(Number(percentage), price) };
      return;
    }

    // Both sides carry a value - only an original to compare against tells us which one changed.
    if (!originalVariant) return;

    const amountChanged = commissionValuesDiffer(amount, originalVariant[amountKey]);
    const percentageChanged = commissionValuesDiffer(percentage, originalVariant[percentageKey]);

    if (amountChanged && !percentageChanged) {
      synced = { ...synced, [percentageKey]: commissionPercentageFromAmount(Number(amount), price) };
    } else if (percentageChanged && !amountChanged) {
      synced = { ...synced, [amountKey]: commissionAmountFromPercentage(Number(percentage), price) };
    }
  });

  return synced;
};
